import { cn } from '@/lib/cn';
import { es } from '@/i18n/es';
import type { ContentType } from '@/types';

interface TypeBadgeProps {
  type: ContentType;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Distinguir fanfic de original NUNCA depende sólo del color: el badge
 * lleva ícono y texto, y funciona igual en escala de grises.
 */
export function TypeBadge({ type, size = 'sm', className }: TypeBadgeProps) {
  const label = type === 'fanfic' ? es.contentType.fanfic : es.contentType.original;
  return (
    <span
      data-content-type={type}
      className={cn(
        'inline-flex items-center gap-1 rounded-pill border border-accent/30 bg-accent-soft',
        'font-medium text-accent',
        size === 'sm' ? 'px-2 py-0.5 text-2xs uppercase tracking-wide' : 'px-2.5 py-1 text-xs',
        className,
      )}
    >
      <TypeIcon type={type} />
      <span>{label}</span>
      <span className="sr-only">{es.a11y.typeBadge(label)}</span>
    </span>
  );
}

/** Ícono distinto por tipo: dos ramas para fanfic, una pluma para original. */
export function TypeIcon({ type, className }: { type: ContentType; className?: string }) {
  if (type === 'fanfic') {
    return (
      <svg
        viewBox="0 0 16 16"
        className={cn('h-3 w-3', className)}
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M4 14V8.5C4 5.5 6.5 3 9.5 3H12M12 3 9.5 1M12 3 9.5 5M4 14V11c0-2 1.6-3.5 3.5-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg
      viewBox="0 0 16 16"
      className={cn('h-3 w-3', className)}
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M2.5 13.5 6 10m0 0c4.5 1 8-2.5 7.5-8-5.5-.5-9 3-8 7.5.2.2.3.4.5.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
